/**
 * 客戶模組 - 輔助函數
 * @module customer/helpers
 * @description 提供客戶管理相關的純函數工具與資料轉換邏輯
 */

import type { Customer, ServiceError } from "./types";
import { discountColorMap, AVATAR_SIZE, AVATAR_QUALITY } from "./constants";

/**
 * 根據折扣百分比返回對應的 Tailwind 顏色類別
 * @param discount - 折扣百分比（40-100）
 * @returns Tailwind CSS 類別字串
 * @example
 * getDiscountColor(65) // "text-cyan-600 font-bold"
 * getDiscountColor(85) // "text-amber-600 font-semibold"
 * getDiscountColor(100) // "text-gray-500"
 */
export const getDiscountColor = (discount: number): string => {
  if (discount <= discountColorMap.high.threshold) {
    return discountColorMap.high.class;
  }
  if (discount <= discountColorMap.medium.threshold) {
    return discountColorMap.medium.class;
  }
  if (discount <= discountColorMap.low.threshold) {
    return discountColorMap.low.class;
  }
  return discountColorMap.none.class;
};

/**
 * 格式化折扣顯示文字（台灣習慣）
 * @param discount - 折扣百分比（40-100）
 * @returns 格式化後的折扣文字
 * @example
 * formatDiscountText(85) // "85折"
 * formatDiscountText(100) // "原價"
 * formatDiscountText(70) // "7折"
 */
export const formatDiscountText = (discount: number): string => {
  if (discount >= 100) {
    return "原價";
  }
  
  // 如果是整十的折扣（如 90, 80, 70），顯示為 9折, 8折, 7折
  if (discount % 10 === 0) {
    return `${discount / 10}折`;
  }
  
  // 其他情況顯示完整數字（如 85折, 75折）
  return `${discount}折`;
};

/**
 * 將資料庫原始資料格式化為 Customer 介面
 * @param dbRecord - 資料庫查詢返回的原始記錄
 * @returns 格式化後的 Customer 物件
 * @description 處理 null 值、日期格式化、預設頭貼生成等邏輯
 */
export const formatCustomerFromDb = (dbRecord: any): Customer => {
  const id = dbRecord.id as string;
  
  return {
    id,
    name: dbRecord.name || "",
    email: dbRecord.email ?? "",
    phone: dbRecord.phone ?? "",
    avatar:
      dbRecord.avatar_url ||
      `https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20portrait&width=100&height=100&seq=${id.slice(
        0,
        8
      )}&orientation=squarish`,
    member_level: (dbRecord.member_level ?? "normal") as Customer["member_level"],
    discount:
      dbRecord.discount !== null && dbRecord.discount !== undefined
        ? Number(dbRecord.discount)
        : 100,
    total_orders: dbRecord.total_orders ?? 0,
    total_spent: dbRecord.total_spent ?? 0,
    registered_at: dbRecord.registered_at
      ? new Date(dbRecord.registered_at).toLocaleDateString("zh-TW")
      : new Date(dbRecord.created_at).toLocaleDateString("zh-TW"),
    last_order_at: dbRecord.last_order_at
      ? new Date(dbRecord.last_order_at).toLocaleDateString("zh-TW")
      : "-",
    status: (dbRecord.status ?? "active") as Customer["status"],
    notes: dbRecord.notes ?? "",
    company: dbRecord.company ?? "",
    tax_id: dbRecord.tax_id ?? "",
    address: dbRecord.address ?? "",
    tags: dbRecord.tags ?? [],
    auth_user_id: dbRecord.auth_user_id ?? null,
  };
};

/**
 * 壓縮並裁切頭貼圖片為正方形
 * @param file - 原始圖片檔案
 * @returns Promise<Blob> - 壓縮後的 WebP 格式 Blob
 * @throws {ServiceError} 當圖片載入失敗或無法建立 canvas 時
 * @description 將圖片裁切為正方形、調整尺寸並轉換為 WebP 格式
 */
export const compressAvatar = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      canvas.width = AVATAR_SIZE;
      canvas.height = AVATAR_SIZE;

      if (!ctx) {
        const error: ServiceError = {
          code: "CANVAS_ERROR",
          message: "無法建立 canvas 上下文",
          details: null,
        };
        reject(error);
        return;
      }

      // 計算裁切區域（取中心正方形）
      const minDim = Math.min(img.width, img.height);
      const sx = (img.width - minDim) / 2;
      const sy = (img.height - minDim) / 2;

      // 繪製白色背景
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);

      // 繪製裁切後的圖片
      ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, AVATAR_SIZE, AVATAR_SIZE);

      // 轉換為 WebP Blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            const error: ServiceError = {
              code: "COMPRESSION_ERROR",
              message: "圖片壓縮失敗",
              details: null,
            };
            reject(error);
          }
        },
        "image/webp",
        AVATAR_QUALITY
      );
    };

    img.onerror = () => {
      const error: ServiceError = {
        code: "IMAGE_LOAD_ERROR",
        message: "圖片載入失敗",
        details: { fileName: file.name },
      };
      reject(error);
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * 生成隨機密碼
 * @param length - 密碼長度（預設 8）
 * @returns 隨機生成的密碼字串
 * @description 使用大小寫字母和數字生成密碼，排除易混淆字元（0, O, I, l, 1）
 */
export const generateRandomPassword = (length: number = 8): string => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

/**
 * 驗證 Email 格式
 * @param email - Email 字串
 * @returns 是否為有效的 Email 格式
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * 驗證電話格式（台灣手機或市話）
 * @param phone - 電話字串
 * @returns 是否為有效的電話格式
 * @description 支援格式：0912-345-678、0912345678、02-12345678、(02)12345678
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^(\(?\d{2,4}\)?[-\s]?)?\d{3,4}[-\s]?\d{3,4}$/;
  return phoneRegex.test(phone);
};

/**
 * 格式化電話號碼（移除特殊字元）
 * @param phone - 原始電話字串
 * @returns 僅包含數字的電話字串
 */
export const formatPhoneForCall = (phone: string): string => {
  return phone.replace(/[-\s()]/g, "");
};

/**
 * 建立 Email 主旨
 * @param customerName - 客戶姓名
 * @returns URL 編碼後的 Email 主旨
 */
export const createEmailSubject = (customerName: string): string => {
  return encodeURIComponent(`親愛的 ${customerName} 您好`);
};

/**
 * 計算折扣後價格
 * @param originalPrice - 原價
 * @param discount - 折扣百分比（40-100）
 * @returns 折扣後價格
 */
export const calculateDiscountedPrice = (
  originalPrice: number,
  discount: number
): number => {
  return Math.round((originalPrice * discount) / 100);
};

/**
 * 格式化貨幣顯示
 * @param amount - 金額
 * @returns 格式化後的貨幣字串（如：NT$ 1,000）
 */
export const formatCurrency = (amount: number): string => {
  return `NT$ ${amount.toLocaleString("zh-TW")}`;
};

/**
 * 生成唯一的檔案名稱
 * @param prefix - 檔案名稱前綴（如：avatars）
 * @param extension - 副檔名（如：webp）
 * @returns 唯一的檔案名稱
 */
export const generateUniqueFileName = (
  prefix: string,
  extension: string
): string => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `${prefix}/${timestamp}_${randomStr}.${extension}`;
};

/**
 * 建立 ServiceError 物件
 * @param code - 錯誤代碼
 * @param message - 錯誤訊息
 * @param details - 詳細資訊（可選）
 * @returns ServiceError 物件
 */
export const createServiceError = (
  code: string,
  message: string,
  details?: unknown
): ServiceError => {
  return { code, message, details };
};

/**
 * 記錄錯誤到控制台（統一格式）
 * @param context - 錯誤發生的上下文（如：CustomerService.createCustomer）
 * @param error - 錯誤物件
 */
export const logError = (context: string, error: unknown): void => {
  console.error(`❌ [${context}] 錯誤:`, error);
  
  if (error instanceof Error) {
    console.error(`   訊息: ${error.message}`);
    console.error(`   堆疊: ${error.stack}`);
  }
};

/**
 * 記錄成功訊息到控制台（統一格式）
 * @param context - 操作上下文
 * @param message - 成功訊息
 */
export const logSuccess = (context: string, message: string): void => {
  console.log(`✅ [${context}] ${message}`);
};

/**
 * 記錄資訊到控制台（統一格式）
 * @param context - 操作上下文
 * @param message - 資訊內容
 */
export const logInfo = (context: string, message: string): void => {
  console.log(`🔵 [${context}] ${message}`);
};
